import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import styles from '../styles/pages/About.module.css';

// ─── 4 OPERATING PRINCIPLES ───
const operatingPrinciples = [
  {
    num: '01',
    title: 'Speed Over Slideware',
    desc: 'Start with a clear scope. Work in two-week sprints. Review systems your engineers can inspect and your operators can run.',
    tag: 'WORKING RELEASES',
  },
  {
    num: '02',
    title: 'Outcome-Based Pricing',
    desc: 'Agree the deliverables and milestones together. Structure engineering engagements around the work that gets shipped.',
    tag: 'AGREED DELIVERABLES',
  },
  {
    num: '03',
    title: 'Forward-Deployed Engineering',
    desc: 'A product lead and senior engineers work inside your tools and repositories, alongside your staff.',
    tag: 'WORKING WITH YOUR TEAM',
  },
  {
    num: '04',
    title: 'People stay in control',
    desc: 'Use agents for research, drafting, and engineering. Keep human approval over consequential changes and verify the result.',
    tag: 'HUMAN REVIEW',
  },
];

// ─── LEADERSHIP SQUAD ───
const leaders = [
  {
    name: 'Josh Johnson',
    role: 'Co-Founder & CEO',
    photo: '/images/josh-johnson.jpg',
    bio: 'Commercial strategy, multi-agent AI architecture, and enterprise transformation. Josh sets company direction and focuses on applying AI to real business work.',
    isUnpictured: false,
  },
  {
    name: 'Sierhah Price',
    role: 'Co-Founder & COO',
    photo: '/images/sierhah-price-headshot.jpg',
    bio: 'Business operations, client success, and delivery planning. Sierhah helps keep client engagements organized and on track.',
    isUnpictured: false,
  },
  {
    name: 'Chris Owens',
    role: 'Chief Services Officer (CSO)',
    photo: '/images/chris-owens3.jpg',
    bio: 'Technical strategy, enterprise software delivery, engineering teams, and systems architecture. Chris oversees software and AI delivery.',
    isUnpictured: false,
  },
  {
    name: 'Maurice Cadenhead',
    role: 'Chief Technology Officer (CTO)',
    photo: '/images/maurice-cadenhead.png',
    photoClassName: styles.leaderPhotoRaised,
    bio: 'Enterprise application and infrastructure architect. Creator of PatientGlue, a healthcare patient engagement service. Maurice oversees foundational architecture and platform decisions.',
    isUnpictured: false,
  },
];

const About = () => {
  return (
    <div className={`page ${styles.aboutPage}`}>
      <SEO
        title="About AURABLOX · Commercial Data Specialists"
        description="Meet the team responsible for commercial data architecture, platform controls, delivery, and operational handover."
        path="/about"
      />

      {/* ─── HERO HEADER ─── */}
      <header className={styles.heroHeader} id="top">
        <div className={styles.heroContent}>
          <div>
            <h1 className={`${styles.heroHeadline} kinetic-title`}>Commercial data.
              Clear ownership.</h1>
            <p className={styles.heroLede}>We focus on enterprise commercial data: the architecture, master data, and operating controls behind trusted reporting and AI readiness. Our core engagement takes a defined Databricks foundation from diagnostic to handover.</p>
            <p className="page-path">Our team <span aria-hidden="true">→</span> Our approach <span aria-hidden="true">→</span> Your data foundation</p>
          </div>
        </div>
      </header>

      <main>
        {/* ─── SECTION 1: LEADERSHIP ─── */}
        <section className="section" id="leadership" aria-labelledby="leadership-heading">
          <div className="section-index-bar" aria-hidden="true">
            <span className="section-index-bar__left"><span className="seal seal--sm" /> 01 // LEADERSHIP</span>
            <span className="section-index-bar__right">FOUNDERS &amp; EXECUTIVE TEAM</span>
          </div>
          <div className="section__header">
            <h2 id="leadership-heading">Leadership</h2>
            <p>
              The people responsible for company direction, operations, engineering, and technology.
            </p>
          </div>

          <div className={styles.leadershipGrid}>
            {leaders.map((l) => (
              <article key={l.name} className={styles.leaderCard}>
                {l.isUnpictured ? (
                  <div className={styles.unpicturedMedia}>
                    <div className={styles.unpicturedMonogram}>M</div>
                    <span className={styles.unpicturedBadge}>UNPICTURED CTO</span>
                  </div>
                ) : (
                  <div className={styles.leaderMedia}>
                    <img src={l.photo} alt={l.name} className={[styles.leaderPhoto, l.photoClassName].filter(Boolean).join(' ')} loading="lazy" />
                  </div>
                )}
                <div className={styles.leaderBody}>
                  <span className={styles.leaderRole}>{l.role}</span>
                  <h3 className={styles.leaderName}>{l.name}</h3>
                  <p className={styles.leaderBio}>{l.bio}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* ─── SECTION 2: OPERATING PRINCIPLES ─── */}
        <section className="section" id="model" aria-labelledby="model-heading">
          <div className="section-index-bar" aria-hidden="true">
            <span className="section-index-bar__left"><span className="seal seal--sm" /> 02 // OPERATING PRINCIPLES</span>
            <span className="section-index-bar__right">HOW WE ENGAGE &amp; SHIP</span>
          </div>
          <div className="section__header">
            <h2 id="model-heading">How We Engage &amp; Ship</h2>
            <p>
              A practical way to move from an agreed problem to a working release.
            </p>
          </div>

          <div className={styles.principlesGrid}>
            {operatingPrinciples.map((p) => (
              <article key={p.num} className={styles.principleCard}>
                <span className={styles.principleNum}>PRINCIPLE {p.num}</span>
                <h3 className={styles.principleTitle}>{p.title}</h3>
                <p className={styles.principleDesc}>{p.desc}</p>
                <span className={styles.principlePill}>{p.tag}</span>
              </article>
            ))}
          </div>
        </section>

        {/* ─── SECTION 3: OPERATIONAL HUB & SCHEDULED VISITS ─── */}
        <section className="section" id="headquarters" aria-labelledby="hq-heading">
          <div className="section-index-bar" aria-hidden="true">
            <span className="section-index-bar__left"><span className="seal seal--sm" /> 03 // OPERATIONAL HUB</span>
            <span className="section-index-bar__right">ST. LOUIS · BALLPARK VILLAGE</span>
          </div>
          <div className="section__header">
            <h2 id="hq-heading">Visit us in St. Louis</h2>
            <p>
              We welcome clients, founders, and engineering teams into our downtown St. Louis office for scheduled working sessions, architecture reviews, and hands-on workshops.
            </p>
          </div>

          <div className={styles.hqShowcase}>
            <div className={styles.hqContent}>
              <span className={styles.heroBadge}>Operational Hub · By Scheduled Visit</span>
              <h3>Spark St. Louis at Ballpark Village</h3>
              <p className={styles.hqDesc}>
                Located on the 9th floor overlooking Busch Stadium and the Gateway Arch, our workspace at Spark St. Louis is set up for whiteboarding, live system audits, and team enablement bootcamps. If you are in St. Louis—or planning a trip in for a project kickoff—let us know ahead of time and we’ll set up a dedicated room for your team.
              </p>

              <div className={styles.hqDetailsGrid}>
                <div className={styles.hqDetailBlock}>
                  <span className={styles.hqDetailLabel}>ADDRESS</span>
                  <address className={styles.hqAddress}>
                    <strong>AURABLOX · Spark St. Louis</strong>
                    <br />
                    Ballpark Village · 6 Cardinal Way, Suite 900
                    <br />
                    St. Louis, MO 63102
                  </address>
                </div>
                <div className={styles.hqDetailBlock}>
                  <span className={styles.hqDetailLabel}>ON-SITE SESSIONS</span>
                  <p className={styles.hqVisitNote}>
                    Commercial data discovery, blueprint reviews, and operator handover sessions available by appointment.
                  </p>
                </div>
              </div>

              <div className={styles.hqActionRow}>
                <Link to="/contact#inquiry" className="btn btn--primary">
                  Schedule an in-person visit ↗
                </Link>
                <a
                  href="https://sparkcoworking.com/st-louis/?cn-reloaded=1"
                  target="_blank"
                  rel="noreferrer"
                  className="btn"
                >
                  Explore Spark St. Louis ↗
                </a>
              </div>
            </div>

            <div className={styles.hqGallery}>
              <figure className={styles.hqPhotoPrimary}>
                <img
                  src="/images/spark-stl-suiteview.jpg"
                  alt="Spark St. Louis Suite 900 workspace overlooking downtown St. Louis and the Gateway Arch"
                  loading="lazy"
                />
                <figcaption>Suite 900 view overlooking downtown St. Louis &amp; the Gateway Arch</figcaption>
              </figure>
              <figure className={styles.hqPhotoSecondary}>
                <img
                  src="/images/spark-stl-facility.jpg"
                  alt="Spark St. Louis commons and hospitality lounge at Ballpark Village"
                  loading="lazy"
                />
                <figcaption>Spark St. Louis commons &amp; workshop lounge at Ballpark Village</figcaption>
              </figure>
            </div>
          </div>
        </section>

        {/* ─── SECTION 4: BOTTOM CTA BANNER ─── */}
        <section className={styles.ctaBanner}>
          <h2 className={styles.ctaHeadline}>
            Start a conversation with our team
          </h2>
          <p className={styles.ctaLede}>
            Tell us which commercial reporting or AI deadline is at risk, and where your data foundation needs to improve.
          </p>
          <div className={styles.ctaButtons}>
            <Link to="/contact#inquiry" className="btn btn--primary">
              Discuss your data foundation ↗
            </Link>
            <Link to="/training" className="btn">
              Explore Enablement Training ↗
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
};

export default About;
